import { type UseQueryResult, useQueries } from "@tanstack/react-query";
import {
  GetOrganizationGoogleCredentialsRequestQuery,
  UserInfoResponse,
} from "common";
import { type JSX, useCallback } from "react";
import {
  fetchOrganizationAssumeRoleCredentials,
  fetchOrganizationGoogleCredentials,
} from "@via/pages/organization/organization.api.tsx";

type Membership = UserInfoResponse["userOrganizations"][number];

export type MembershipConnection = {
  id: number;
  name: string;
  // "Name (Organization name)", what a picker shows.
  label: string;
  organizationId: string;
  organizationName: string;
};

export type MembershipConnections = {
  connections: MembershipConnection[];
  isLoading: boolean;
  // Organizations whose list could not be loaded. The others still show.
  failedOrganizationNames: string[];
};

const activeMemberships = (user: UserInfoResponse | undefined): Membership[] =>
  (user?.userOrganizations ?? []).filter(o => o.status === "active");

/**
 * One query per active membership, merged (#1135). The pickers used to read userOrganizations[0],
 * which is whichever membership sorts first, so a member of several organizations never saw the
 * others' saved connections. The backend accepts any of them at use time: it looks a picked id up
 * across every group the user is in. The query keys match the single organization hooks in
 * organization.api.tsx, so the cache and their invalidations are shared.
 */
const useAcrossMemberships = (
  user: UserInfoResponse | undefined,
  enabled: boolean,
  queryFor: (organizationId: string) => {
    queryKey: unknown[];
    queryFn: () => Promise<{ id: number; name: string }[]>;
  }
): MembershipConnections => {
  const memberships = enabled ? activeMemberships(user) : [];
  const membershipsKey = memberships.map(m => `${m.id}:${m.name}`).join("|");
  // A stable combine lets useQueries return the same object until a result changes, so a
  // picker's effect that depends on the list does not run on every render.
  const combine = useCallback(
    (results: UseQueryResult<{ id: number; name: string }[]>[]) => {
      const connections: MembershipConnection[] = [];
      const seen = new Set<number>();
      const failedOrganizationNames: string[] = [];
      results.forEach((result, index) => {
        const organization = memberships[index];
        if (result.isError) failedOrganizationNames.push(organization.name);
        for (const c of result.data ?? []) {
          if (seen.has(c.id)) continue;
          seen.add(c.id);
          connections.push({
            id: c.id,
            name: c.name,
            label: `${c.name} (${organization.name})`,
            organizationId: organization.id,
            organizationName: organization.name,
          });
        }
      });
      return {
        connections,
        isLoading: results.some(r => r.isLoading),
        failedOrganizationNames,
      };
    },
    // memberships is rebuilt every render; its content is what matters.
    [membershipsKey]
  );

  return useQueries({
    queries: memberships.map(m => ({ ...queryFor(m.id), retry: false })),
    combine,
  });
};

export const useMembershipGoogleConnections = (
  user: UserInfoResponse | undefined,
  query: GetOrganizationGoogleCredentialsRequestQuery,
  options?: { enabled?: boolean }
): MembershipConnections =>
  useAcrossMemberships(user, options?.enabled ?? true, organizationId => ({
    queryKey: [
      `/api/organization/v1/${organizationId}/google-credentials`,
      query,
    ],
    queryFn: () =>
      fetchOrganizationGoogleCredentials({ organizationId, query }),
  }));

export const useMembershipAssumeRoleConnections = (
  user: UserInfoResponse | undefined,
  options?: { enabled?: boolean }
): MembershipConnections =>
  useAcrossMemberships(user, options?.enabled ?? true, organizationId => ({
    queryKey: [
      `/api/organization/v1/${organizationId}/assume-role-credentials`,
    ],
    queryFn: () => fetchOrganizationAssumeRoleCredentials({ organizationId }),
  }));

/** Shown under a picker when some organization's list failed; the rest of the list still works. */
export const MembershipConnectionsWarning = ({
  names,
}: {
  names: string[];
}): JSX.Element | null =>
  names.length === 0 ? null : (
    <p
      className="mt-1 text-xs text-amber-700"
      data-testid="membership-connections-warning">
      Could not load saved connections from {names.join(", ")}.
    </p>
  );
